import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { saleAgreementAPI } from '../services/api';
import { downloadBlob } from '../utils/download';
import { formatDate } from '../utils/dateFormat';
import { formatSaleAmount } from '../utils/horseSaleIncome';

// Mirrors PAYMENT_METHODS / VET_RECORDS_OPTIONS in backend/models/horse_sale_agreement.py.
const PAYMENT_METHODS = [
  ['cash', 'Cash'],
  ["cashiers_check", "Cashiers' Check"],
  ['wire', 'Bank wire transfer'],
  ['paypal', 'PayPal'],
  ['venmo', 'Venmo'],
  ['cashapp', 'Cash App'],
];
const VET_RECORDS = [
  ['not_treated', 'Not treated or examined by a vet while owned by us'],
  ['all_provided', 'Treated — buyer given all vet records'],
  ['some_provided', 'Treated — buyer given some records (incomplete)'],
  ['none_provided', 'Treated — buyer given no vet records'],
];

const Section = ({ title, children }) => (
  <Box sx={{ mt: 2.5 }}>
    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>{title}</Typography>
    {children}
  </Box>
);

const Row = ({ children }) => (
  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.5 }}>{children}</Stack>
);

const pdfFilename = (horse) => {
  const name = (horse?.registered_name || horse?.name || `Horse ${horse?.id}`).trim();
  return `Sales_Agreement_${name.replace(/[^A-Za-z0-9]+/g, '_').replace(/^_|_$/g, '')}.pdf`;
};

// An error from a responseType:'blob' request carries its JSON body as a Blob.
const errorMessage = async (err, fallback) => {
  const data = err.response?.data;
  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text()).error || fallback;
    } catch {
      return fallback;
    }
  }
  return data?.error || fallback;
};

/**
 * Fill in and download the Equine Sales Agreement (Bill of Sale) for a horse
 * that is sale pending or sold. Buyer and terms are saved per horse; horse
 * facts, the price and the seller block come from the horse and Business
 * records and are edited there, not here.
 */
function SaleAgreementDialog({ horse, onClose }) {
  const [terms, setTerms] = useState(null);
  const [summary, setSummary] = useState(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(null); // null | 'save' | 'download'
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    let cancelled = false;
    saleAgreementAPI.get(horse.id)
      .then((res) => {
        if (cancelled) return;
        setTerms(res.data.terms);
        setSummary(res.data.horse);
        setSaved(res.data.saved);
      })
      .catch(async (err) => {
        if (!cancelled) setError(await errorMessage(err, 'Could not load the sales agreement.'));
      });
    return () => { cancelled = true; };
  }, [horse.id]);

  const set = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setTerms((prev) => ({ ...prev, [field]: value }));
  };

  const toggleMethod = (field, method) => (e) => {
    setTerms((prev) => {
      const current = new Set(prev[field] || []);
      if (e.target.checked) current.add(method); else current.delete(method);
      return { ...prev, [field]: [...current] };
    });
  };

  // These fields hold the *buyer's* details, so browser autofill can only insert
  // the wrong person — it once split "Gina Davis" into Name and Email. Chrome
  // ignores autoComplete="off" on name/address-looking fields but will not fill
  // one whose value isn't a real autocomplete token.
  const text = (field, label, props = {}) => (
    <TextField
      label={label}
      name={`sale-agreement-${field}`}
      autoComplete="off-sale-agreement"
      size="small"
      fullWidth
      value={terms[field] ?? ''}
      onChange={set(field)}
      {...props}
    />
  );
  const money = (field, label) => text(field, label, {
    type: 'number',
    inputProps: { min: 0, step: '0.01' },
    InputProps: { startAdornment: <InputAdornment position="start">$</InputAdornment> },
  });
  const dateField = (field, label) => text(field, label, { type: 'date', InputLabelProps: { shrink: true } });

  const save = async () => {
    // Everything but the server-owned bookkeeping fields.
    const { id, horse_id, created_at, updated_at, created_by, updated_by, ...body } = terms;
    const res = await saleAgreementAPI.save(horse.id, body);
    setTerms(res.data);
    setSaved(true);
  };

  const handleSave = async () => {
    setBusy('save');
    setError('');
    setNotice('');
    try {
      await save();
      setNotice('Saved.');
    } catch (err) {
      setError(await errorMessage(err, 'Could not save the sales agreement.'));
    } finally {
      setBusy(null);
    }
  };

  const handleDownload = async () => {
    setBusy('download');
    setError('');
    setNotice('');
    try {
      await save();
      const res = await saleAgreementAPI.downloadPdf(horse.id);
      downloadBlob(res.data, pdfFilename(horse));
      setNotice('Saved and downloaded.');
    } catch (err) {
      setError(await errorMessage(err, 'Could not generate the PDF.'));
    } finally {
      setBusy(null);
    }
  };

  const methodBoxes = (field) => (
    <FormGroup row>
      {PAYMENT_METHODS.map(([value, label]) => (
        <FormControlLabel
          key={value}
          control={(
            <Checkbox
              size="small"
              checked={(terms[field] || []).includes(value)}
              onChange={toggleMethod(field, value)}
            />
          )}
          label={label}
        />
      ))}
    </FormGroup>
  );

  const displayName = horse.registered_name || horse.name;
  const registrations = (summary?.registrations || [])
    .map((r) => `${r.org ? `${r.org} ` : ''}#${r.number}`).join('; ');

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Sales Agreement — {displayName}</DialogTitle>
      {/* Outside DialogContent so they stay in view: Save and Download sit at the
          bottom, and an alert inside the scroll area is off-screen when they're used. */}
      {(error || notice) && (
        <Box sx={{ px: 3, pb: 2 }}>
          {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
          {notice && <Alert severity="success" onClose={() => setNotice('')}>{notice}</Alert>}
        </Box>
      )}
      <DialogContent dividers>
        {!terms ? (
          !error && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : (
          <>
            {!saved && (
              <Alert severity="info" sx={{ mb: 1 }}>
                Nothing saved for this horse yet — using defaults from the horse record.
              </Alert>
            )}

            <Section title="From the horse record (edit the horse to change these)">
              <Typography variant="body2">
                {[summary?.type, summary?.breed, summary?.color].filter(Boolean).join(' · ') || '—'}
                {summary?.date_of_birth && ` · foaled ${summary.date_of_birth.slice(0, 4)}`}
              </Typography>
              <Typography variant="body2">
                Registration: {registrations || 'none'} · Microchip: {summary?.microchip || 'none'}
              </Typography>
              <Typography variant="body2">
                Purchase price: {summary?.sale_amount != null ? formatSaleAmount(summary.sale_amount) : 'not set (left blank on the form)'}
                {summary?.date_sold && ` · sold ${formatDate(summary.date_sold)}`}
              </Typography>
            </Section>

            <Section title="Agreement">
              <Row>{dateField('agreement_date', 'Agreement date')}<Box sx={{ flex: 1 }} /></Row>
            </Section>

            <Divider sx={{ mt: 1 }} />
            <Section title="Buyer">
              <Row>{text('buyer_name', 'Name')}</Row>
              <Row>{text('buyer_street', 'Street address')}</Row>
              <Row>
                {text('buyer_city', 'City')}
                {text('buyer_state', 'State', { sx: { maxWidth: { sm: 120 } } })}
                {text('buyer_zip', 'Zip', { sx: { maxWidth: { sm: 140 } } })}
              </Row>
              <Row>
                {text('buyer_phone', 'Telephone')}
                {text('buyer_other_phone', 'Other telephone')}
                {text('buyer_fax', 'Fax')}
              </Row>
              <Row>{text('buyer_email', 'Email', { type: 'email' })}</Row>
            </Section>

            <Divider sx={{ mt: 1 }} />
            <Section title="Horse (§2.1)">
              <Row>{text('color_markings', 'Color and markings')}</Row>
              <Row>{text('identifying_marks', 'Tattoos, brands or other identifying marks')}</Row>
              <FormControlLabel
                control={<Checkbox size="small" checked={Boolean(terms.has_passport)} onChange={set('has_passport')} />}
                label="Horse has a passport"
              />
              {terms.has_passport && (
                <Row>{text('passport_number', 'Passport #')}{text('passport_country', 'Country')}</Row>
              )}
            </Section>

            <Section title="Veterinary records (§2.3)">
              <RadioGroup value={terms.vet_records || ''} onChange={set('vet_records')}>
                {VET_RECORDS.map(([value, label]) => (
                  <FormControlLabel key={value} value={value} control={<Radio size="small" />} label={label} />
                ))}
              </RadioGroup>
            </Section>

            <Section title="Ownership (§2.5)">
              <Row>{text('ownership_exceptions', 'Exceptions to clear title (liens, co-owners)')}</Row>
            </Section>

            <Divider sx={{ mt: 1 }} />
            <Section title="Deposit (§3) — non-refundable">
              <Row>{money('deposit_amount', 'Deposit amount')}<Box sx={{ flex: 1 }} /></Row>
              {methodBoxes('deposit_methods')}
            </Section>

            <Section title="Payment of purchase price (§4.2–4.3)">
              {methodBoxes('payment_methods')}
              <Row>{text('payment_method_other', 'Other payment method')}</Row>
              <Row>
                <TextField
                  select
                  size="small"
                  label="Payment plan"
                  value={terms.payment_plan || ''}
                  onChange={set('payment_plan')}
                  sx={{ minWidth: 220 }}
                >
                  <MenuItem value=""><em>Not chosen</em></MenuItem>
                  <MenuItem value="lump_sum">Lump sum</MenuItem>
                  <MenuItem value="installments">Monthly installments</MenuItem>
                </TextField>
                {terms.payment_plan === 'lump_sum' && dateField('lump_sum_due_date', 'Due on')}
              </Row>
              {terms.payment_plan === 'installments' && (
                <>
                  <Row>
                    {money('installment_first_amount', 'First installment')}
                    {dateField('installment_first_due_date', 'Due on or before')}
                  </Row>
                  <Row>
                    {money('installment_amount', 'Then each month')}
                    {text('installment_day_of_month', 'On day of month (1–28)', {
                      type: 'number', inputProps: { min: 1, max: 28 },
                    })}
                  </Row>
                </>
              )}
            </Section>

            <Section title="Late payments (§4.8)">
              <Row>
                {money('late_fee', 'Late fee')}
              </Row>
            </Section>

            <Section title="Registration (§5)">
              <RadioGroup
                row
                value={terms.registered ? 'yes' : 'no'}
                onChange={(e) => setTerms((prev) => ({ ...prev, registered: e.target.value === 'yes' }))}
              >
                <FormControlLabel value="yes" control={<Radio size="small" />} label="Registered (as in §2.1)" />
                <FormControlLabel value="no" control={<Radio size="small" />} label="Not registered" />
              </RadioGroup>
            </Section>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              Anything left empty prints as a blank line to fill in by hand. The seller block comes
              from Business Info.
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={Boolean(busy)}>Close</Button>
        <Button onClick={handleSave} disabled={!terms || Boolean(busy)}>
          {busy === 'save' ? 'Saving…' : 'Save'}
        </Button>
        <Button
          variant="contained"
          startIcon={busy === 'download' ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
          onClick={handleDownload}
          disabled={!terms || Boolean(busy)}
        >
          Save &amp; Download PDF
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SaleAgreementDialog;
